import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { DailyStatus } from '@/types/task';
import { useToast } from '@/hooks/use-toast';
import { format, startOfMonth, endOfMonth } from 'date-fns';

export function useStatusHistory(month: Date) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [statusesByDate, setStatusesByDate] = useState<Record<string, DailyStatus>>({});
  const [loading, setLoading] = useState(true);

  const fetchStatuses = useCallback(async () => {
    if (!user) {
      setStatusesByDate({});
      setLoading(false);
      return;
    }

    const start = format(startOfMonth(month), 'yyyy-MM-dd');
    const end = format(endOfMonth(month), 'yyyy-MM-dd');

    try {
      const { data, error } = await supabase
        .from('daily_status')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', start)
        .lte('date', end);

      if (error) throw error;

      const byDate: Record<string, DailyStatus> = {};
      data?.forEach((status) => {
        byDate[status.date] = status as DailyStatus;
      });
      setStatusesByDate(byDate);
    } catch (error) {
      console.error('Error fetching status history:', error);
    } finally {
      setLoading(false);
    }
  }, [user, month]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const updateStatus = async (id: string, content: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('daily_status')
        .update({ content })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      setStatusesByDate((prev) => {
        const updated = { ...prev };
        for (const date in updated) {
          if (updated[date].id === id) {
            updated[date] = { ...updated[date], content };
            break;
          }
        }
        return updated;
      });

      toast({
        title: 'Notes updated',
        description: 'Your daily notes have been saved.',
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error updating notes',
        description: 'Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteStatus = async (id: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('daily_status')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      setStatusesByDate((prev) => {
        const updated = { ...prev };
        for (const date in updated) {
          if (updated[date].id === id) {
            delete updated[date];
            break;
          }
        }
        return updated;
      });

      toast({
        title: 'Notes deleted',
        description: 'Your daily notes have been removed.',
      });
    } catch (error) {
      console.error('Error deleting status:', error);
      toast({
        title: 'Error deleting notes',
        description: 'Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  return {
    statusesByDate,
    loading,
    updateStatus,
    deleteStatus,
    refetch: fetchStatuses,
  };
}
